import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_BASE_URL = "";
const API = `${API_BASE_URL}/api`;

const RESET_COPY = {
  fr: {
    mismatch: "Les mots de passe ne correspondent pas",
    shortPassword: "Le mot de passe doit contenir au moins 8 caractères",
    success: "Mot de passe réinitialisé avec succès",
    tokenError: "Token invalide ou expiré",
    invalidTitle: "Lien invalide",
    invalidMessage: "Ce lien de réinitialisation n'est pas valide.",
    requestNew: "Demander un nouveau lien",
    title: "Nouveau mot de passe",
    subtitle: "Choisissez un nouveau mot de passe",
    newPassword: "Nouveau mot de passe",
    passwordPlaceholder: "Min. 8 caractères",
    confirm: "Confirmer",
    loading: "Réinitialisation...",
    submit: "Réinitialiser",
    showPassword: "Afficher le mot de passe",
    hidePassword: "Masquer le mot de passe",
  },
  en: {
    mismatch: "Passwords do not match",
    shortPassword: "Password must contain at least 8 characters",
    success: "Password reset successfully",
    tokenError: "Invalid or expired token",
    invalidTitle: "Invalid link",
    invalidMessage: "This reset link is not valid.",
    requestNew: "Request a new link",
    title: "New password",
    subtitle: "Choose a new password",
    newPassword: "New password",
    passwordPlaceholder: "Min. 8 characters",
    confirm: "Confirm",
    loading: "Resetting...",
    submit: "Reset password",
    showPassword: "Show password",
    hidePassword: "Hide password",
  },
};

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const copy = RESET_COPY[language] || RESET_COPY.fr;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(copy.mismatch);
      return;
    }

    if (formData.password.length < 8) {
      toast.error(copy.shortPassword);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.password
      });
      toast.success(copy.success);
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || copy.tokenError);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Header />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">{copy.invalidTitle}</h1>
            <p className="text-zinc-500 mb-6">{copy.invalidMessage}</p>
            <Link to="/forgot-password" className="text-[#CCFF00] hover:underline">
              {copy.requestNew}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      <div className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-[#18181B] border border-white/10 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black uppercase text-white mb-2">{copy.title}</h1>
              <p className="text-zinc-500">{copy.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-white text-sm font-medium">{copy.newPassword}</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={8}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] pr-10"
                    placeholder={copy.passwordPlaceholder}
                    data-testid="reset-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    aria-label={showPassword ? copy.hidePassword : copy.showPassword}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-white text-sm font-medium">{copy.confirm}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                  placeholder="••••••••"
                  data-testid="reset-confirm-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#CCFF00] text-black py-3 font-bold uppercase tracking-wide hover:bg-[#AACC00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reset-submit"
              >
                {loading ? copy.loading : copy.submit}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
